import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Accordion, Button, Dropdown, DropdownButton, Alert, Spinner, Badge } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { getUserBookingHistory, downloadTicket } from "../services/BookingService";
import { RootState } from "../states/rootReducer";
import CommunicationButtons from "../components/CommunicationButtons";
import FeedbackButtons from "../components/FeedbackButtons";
import CustomerSupportWidget from "../components/CustomerSupportWidget";

function BookingHistory() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const months = [
    { value: "01", label: "January" }, { value: "02", label: "February" }, { value: "03", label: "March" },
    { value: "04", label: "April" }, { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" }, { value: "09", label: "September" },
    { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookingHistory();
    }
  }, [isAuthenticated, selectedYear, selectedMonth]);

  const fetchBookingHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const year = selectedYear !== "All" ? selectedYear : undefined;
      const month = selectedMonth !== "All" ? selectedMonth : undefined;
      const data = await getUserBookingHistory(year, month);
      setBookings(data.bookings || []);
      setAvailableYears(data.availableYears || []);
      setAvailableMonths(data.availableMonths || []);
    } catch (error: any) {
      setError('Failed to fetch booking history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTicket = async (bookingId: string) => {
    setDownloading(bookingId);
    try {
      const blob = await downloadTicket(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${bookingId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      setError('Failed to download ticket');
    } finally {
      setDownloading(null);
    }
  };

  const canDownloadTicket = (booking: any) => {
    return booking.status === 'confirmed' && 
           new Date(booking.scheduleId?.startDate) >= new Date();
  };

  const canShowContactButtons = (booking: any) => {
    return booking.status !== 'confirmed' && 
           new Date(booking.scheduleId?.startDate) >= new Date();
  };

  const isEventPassed = (booking: any) => {
    return new Date(booking.scheduleId?.startDate) < new Date();
  };

  const canShowFeedback = (booking: any) => {
    return booking.status === 'confirmed' && isEventPassed(booking);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  if (!isAuthenticated) {
    return (
      <PermissionWrapper pageName="BookingHistoryPage">
        <div className="container">
          <Alert variant="warning">Please log in to view your booking history.</Alert>
        </div>
      </PermissionWrapper>
    );
  }

  return (
    <PermissionWrapper pageName="BookingHistoryPage">
      <div className="container">
        <div className="d-flex justify-content-between mb-4">
          <h2>My Booking History</h2>
          <div className="d-flex gap-2">
            <DropdownButton title={`Year: ${selectedYear}`} variant="outline-primary">
              <Dropdown.Item onClick={() => setSelectedYear("All")}>All Years</Dropdown.Item>
              {availableYears.map(year => (
                <Dropdown.Item key={year} onClick={() => setSelectedYear(year)}>
                  {year}
                </Dropdown.Item>
              ))}
            </DropdownButton>
            
            <DropdownButton title={`Month: ${selectedMonth === "All" ? "All" : months.find(m => m.value === selectedMonth)?.label}`} variant="outline-primary">
              <Dropdown.Item onClick={() => setSelectedMonth("All")}>All Months</Dropdown.Item>
              {availableMonths.map(month => {
                const monthLabel = months.find(m => m.value === month)?.label;
                return (
                  <Dropdown.Item key={month} onClick={() => setSelectedMonth(month)}>
                    {monthLabel}
                  </Dropdown.Item>
                );
              })}
            </DropdownButton>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading...</p>
          </div>
        ) : bookings.length === 0 ? (
          <Alert variant="info">No bookings found.</Alert>
        ) : (
          <Accordion>
            {bookings.map((booking, index) => (
              <Accordion.Item key={booking._id} eventKey={index.toString()}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>
                      <strong>{booking.scheduleId?.programId?.title || 'Event'}</strong>
                      <div className="text-muted small">
                        {new Date(booking.scheduleId?.startDate).toLocaleDateString()} at {booking.scheduleId?.timing}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg={getStatusVariant(booking.status || 'pending')}>
                        {booking.status?.toUpperCase()}
                      </Badge>
                      <span className="fw-bold">₹{booking.totalAmount}</span>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="row">
                    <div className="col-md-8">
                      <p><strong>Venue:</strong> {booking.scheduleId?.address}</p>
                      <p><strong>Organizer:</strong> {booking.scheduleId?.organizerName}</p>
                      <p><strong>Booking Code:</strong> {booking.bookingCode}</p>
                      
                      <h6 className="mt-3">Tickets</h6>
                      {booking.tickets?.map((ticket: any, idx: number) => (
                        <div key={idx} className="mb-2">
                          <span className="badge bg-light text-dark me-2">
                            {ticket.quantity}x {ticket.ticketOptionId?.ticketName}
                          </span>
                          <span>₹{ticket.ticketOptionId?.price} each</span>
                        </div>
                      ))}
                      
                      <div className="mt-3">
                        <strong>Total: ₹{booking.totalAmount}</strong>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      {canDownloadTicket(booking) && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleDownloadTicket(booking._id)}
                          disabled={downloading === booking._id}
                          className="mb-2"
                        >
                          {downloading === booking._id ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              Downloading...
                            </>
                          ) : (
                            'Download Ticket'
                          )}
                        </Button>
                      )}
                      
                      {canShowContactButtons(booking) && booking.scheduleId?.organizerPhone && (
                        <CommunicationButtons
                          variant="contact"
                          size="sm"
                          className="mb-2"
                          data={{
                            organizerName: booking.scheduleId?.organizerName || 'Organizer',
                            organizerPhone: booking.scheduleId?.organizerPhone,
                            eventName: booking.scheduleId?.programId?.title || 'Event',
                            eventDate: new Date(booking.scheduleId?.startDate).toLocaleDateString(),
                            eventTime: booking.scheduleId?.timing || '',
                            venue: booking.scheduleId?.address || '',
                            customerName: booking.customerName || 'Customer',
                            customerPhone: booking.phoneNumber || '',
                            customerEmail: ''
                          }}
                        />
                      )}
                      
                      {canShowFeedback(booking) && (
                        <FeedbackButtons
                          booking={{
                            bookingCode: booking.bookingCode,
                            customerName: booking.customerName
                          }}
                          event={{
                            title: booking.scheduleId?.programId?.title || 'Event',
                            date: new Date(booking.scheduleId?.startDate).toLocaleDateString()
                          }}
                          size="sm"
                          variant="outline-primary"
                          className="mb-2"
                        />
                      )}
                      
                      {booking.status === 'confirmed' && !canDownloadTicket(booking) && !canShowFeedback(booking) && (
                        <small className="text-muted d-block">Event has passed</small>
                      )}
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
        
        {/* Customer Support Widget */}
        {/* <CustomerSupportWidget position="bottom-right" /> */}
      </div>
    </PermissionWrapper>
  );
}

export default BookingHistory;